// Terms of sentences: word order, clause parts, questions, coordination, relative and subordinate
// clauses, adpositions (Kielioppi § 6, 06-sentences.mdx; lessons 12, 16, 17, 46, 47).
// HV facts as 06-sentences.mdx states them (wiki: High_Valyrian_Grammar, Tips & Notes).
import { defineTerms } from './define';

export const terms = defineTerms({
  subjekti: {
    fi: {
      name: 'subjekti',
      short: '**Subjekti** on lauseen tekijä tai se, josta lause kertoo, kuten *mies* lauseessa *mies näkee naisen*.',
      compare: 'Kuten suomessa; valyriassa subjekti on nominatiivissa, ja subjektipronomini jää yleensä pois.',
    },
    en: {
      name: 'subject',
      short: 'The **subject** is the doer, or what the clause is about, like *the man* in *the man sees the woman*.',
      compare: 'As in Finnish; in High Valyrian the subject is nominative, and a subject pronoun is usually dropped.',
    },
    related: ['objekti', 'nominatiivi', 'lauseenjasen'],
    firstIn: 'word-order',
  },
  objekti: {
    fi: {
      name: 'objekti',
      short: '**Objekti** on teon kohde, kuten *naisen* lauseessa *mies näkee naisen*.',
      compare: 'Valyriassa objekti on akkusatiivissa; suomen partitiiviobjektia (*luin kirjaa*, *en näe miestä*) ei ole.',
      long: 'Suoran objektin lisäksi puhutaan epäsuorasta objektista, joka on datiivissa. Applikatiivi voi tehdä paikasta tai edunsaajasta objektin.',
    },
    en: {
      name: 'object',
      short: 'The **object** is the target of the action, like *the woman* in *the man sees the woman*.',
      compare: 'In High Valyrian the object is accusative; there is no Finnish partitive object (*luin kirjaa*, *en näe miestä*).',
      long: 'Besides the direct object there is the indirect object, in the dative. An applicative can turn a place or a beneficiary into an object.',
    },
    related: ['akkusatiivi', 'epasuora-objekti', 'subjekti', 'kokonaisobjekti'],
    firstIn: 'word-order',
  },
  'epasuora-objekti': {
    fi: {
      name: 'epäsuora objekti',
      short: '**Epäsuora objekti** on se, jolle jotakin annetaan tai tehdään, kuten *pojalle* lauseessa *annan pojalle kukan*.',
      compare:
        'Suomen koulukieliopissa *pojalle* on adverbiaali allatiivissa; valyriassa epäsuora objekti on datiivissa ja tulee yleensä suoran objektin edelle.',
    },
    en: {
      name: 'indirect object',
      short: 'The **indirect object** is the one something is given to or done for, like *the boy* in *I give the boy a flower*.',
      compare:
        'Finnish school grammar calls *pojalle* an adverbial in the allative; in High Valyrian the indirect object is dative and usually comes before the direct object.',
    },
    related: ['objekti', 'datiivi', 'sanajarjestys'],
    firstIn: 'word-order',
  },
  lauseenjasen: {
    fi: {
      name: 'lauseenjäsen',
      short: '**Lauseenjäsen** on lauseen osa tehtävänsä mukaan nimettynä: subjekti, objekti, predikaatti eli verbi, adverbiaali.',
      compare: 'Samat nimet kuin suomen koulukieliopissa.',
    },
    en: {
      name: 'clause element',
      short: 'A **clause element** is a part of the clause named by its role: subject, object, predicate (the verb), adverbial.',
      compare: 'The same names as in Finnish school grammar.',
    },
    related: ['subjekti', 'objekti', 'sanajarjestys'],
    firstIn: 'word-order',
  },
  sanajarjestys: {
    fi: {
      name: 'sanajärjestys',
      short: '**Sanajärjestys** on lauseenjäsenten järjestys lauseessa.',
      compare: 'Suomessa verbi on tavallisesti toisena, valyriassa viimeisenä; sijapäätteiden ansiosta järjestys voi silti joustaa kummassakin kielessä.',
      long: 'Valyrian tavallinen järjestys on ajan tai paikan ilmaus, subjekti, tapaa kuvaava adverbi, epäsuora objekti, suora objekti, muu adverbi ja verbi.',
    },
    en: {
      name: 'word order',
      short: '**Word order** is the order of the parts of a clause.',
      compare: 'In Finnish the verb usually comes second, in High Valyrian last; thanks to the case endings the order can still flex in both.',
      long: 'The usual High Valyrian order is a time or place expression, the subject, a manner adverb, the indirect object, the direct object, another adverb, and the verb.',
    },
    related: ['sov', 'lauseenjasen', 'sija'],
    firstIn: 'word-order',
  },
  sov: {
    fi: {
      name: 'SOV',
      short: '**SOV** tarkoittaa sanajärjestystä subjekti – objekti – verbi: ’mies naisen näkee’.',
      compare: 'Suomen tavallinen järjestys on SVO: *mies näkee naisen*.',
    },
    en: {
      name: 'SOV',
      short: '**SOV** means the word order subject – object – verb: ‘the man the woman sees’.',
      compare: 'The usual Finnish order is SVO: *mies näkee naisen*.',
    },
    related: ['sanajarjestys', 'subjekti', 'objekti'],
    firstIn: 'word-order',
  },
  postpositio: {
    fi: {
      name: 'postpositio',
      short: '**Postpositio** on sana, joka tulee täydennyksensä perään, kuten *kanssa* ilmauksessa *ystävän kanssa*.',
      compare: 'Kuten suomessa, valyriankin postposition edellä on usein genetiivi.',
    },
    en: {
      name: 'postposition',
      short: 'A **postposition** is a word that follows its complement, like *kanssa* in Finnish *ystävän kanssa* ‘with a friend’.',
      compare: 'As in Finnish, a High Valyrian postposition often follows a genitive.',
    },
    related: ['prepositio', 'adpositio', 'genetiivi'],
    firstIn: 'cases',
  },
  prepositio: {
    fi: {
      name: 'prepositio',
      short: '**Prepositio** on sana, joka tulee täydennyksensä edelle, kuten englannin *in* ilmauksessa *in the house*.',
      compare: 'Suomessa prepositioita on vähän (*ilman rahaa*); valyriassa esimerkiksi ’sisään’ ja ’ulos’ ilmaistaan prepositioilla.',
    },
    en: {
      name: 'preposition',
      short: 'A **preposition** is a word that comes before its complement, like *in* in *in the house*.',
      compare: 'Finnish has few (*ilman rahaa*); in High Valyrian ‘into’ and ‘out of’, for example, are prepositions.',
    },
    related: ['postpositio', 'adpositio'],
    firstIn: 'cases',
  },
  adpositio: {
    fi: {
      name: 'adpositio',
      short: '**Adpositio** on yhteisnimi prepositioille ja postpositioille.',
      compare: 'Valyriassa osa adpositioista voi siirtyä verbin etuliitteeksi, mitä suomessa tapahtuu vain harvoin.',
    },
    en: {
      name: 'adposition',
      short: '**Adposition** is the common name for prepositions and postpositions.',
      compare: 'In High Valyrian some adpositions can move into the verb as a prefix, which Finnish seldom does.',
    },
    related: ['postpositio', 'prepositio', 'etuliite'],
    firstIn: 'prefixes',
  },
  kysymyssana: {
    fi: {
      name: 'kysymyssana',
      short: '**Kysymyssana** aloittaa kysymyksen: *kuka*, *mikä*, *milloin*.',
      compare: 'Kuten suomessa, se tulee lauseen alkuun; valyrian kysymyssanat alkavat *sp-* (elollinen) tai *sk-* (eloton).',
      long: 'Kyllä–ei-kysymyksessä ei ole suomen *-ko/-kö*-liitteen vastinetta: sanajärjestys on sama kuin väitteessä, ja kysymyksen kuulee nousevasta sävelkulusta.',
    },
    en: {
      name: 'question word',
      short: 'A **question word** starts a question: *who*, *what*, *when*.',
      compare: 'As in Finnish it comes first; High Valyrian question words start with *sp-* (animate) or *sk-* (inanimate).',
      long: 'A yes–no question has nothing like Finnish *-ko/-kö*: the word order is that of a statement, and the rising pitch makes it a question.',
    },
    related: ['elollisuus'],
    firstIn: 'questions',
  },
  rinnastus: {
    fi: {
      name: 'rinnastus',
      short: '**Rinnastus** on sanojen tai lauseiden liittämistä yhteen tasavertaisina, kuten *mies ja nainen*.',
      compare: 'Suomessa rinnastetaan sanalla *ja*; valyriassa yksittäisiä sanoja rinnastetaan tavallisesti konjunktiivisella pidennyksellä.',
    },
    en: {
      name: 'coordination',
      short: '**Coordination** joins words or clauses as equals, as in *a man and a woman*.',
      compare: 'Finnish coordinates with *ja*; High Valyrian usually joins single words with the conjunctive lengthening.',
    },
    related: ['konjunktiivinen-pidennys'],
    firstIn: 'coordination',
  },
  'konjunktiivinen-pidennys': {
    fi: {
      name: 'konjunktiivinen pidennys',
      short: '**Konjunktiivinen pidennys** on valyrian tapa sanoa ’ja’: luettelon viimeisen sanan loppuvokaali pitenee, ja paino siirtyy sille.',
      compare: 'Suomessa ei ole vastinetta; huomaa, että pidentynyt muoto voi näyttää toiselta sijalta, esimerkiksi lokatiivilta.',
      long: 'Nimi tulee sanasta konjunktio ’sidesana’; se ei liity subjunktiiviin. Vain viimeinen sana muuttuu, ja keino toimii substantiiveilla, adjektiiveilla ja verbeillä.',
    },
    en: {
      name: 'conjunctive lengthening',
      short:
        'The **conjunctive lengthening** is the High Valyrian way to say ‘and’: the last vowel of the last word in a list lengthens, and the stress moves onto it.',
      compare: 'Finnish has nothing like it; note that the lengthened form can look like another case, such as the locative.',
      long: 'The name comes from ‘conjunction’; it has nothing to do with the subjunctive. Only the last word changes, and it works with nouns, adjectives and verbs.',
    },
    related: ['rinnastus', 'paino', 'pitka-vokaali'],
    firstIn: 'coordination',
  },
  paalause: {
    fi: {
      name: 'päälause',
      short: '**Päälause** on lause, joka voi olla yksinään, kuten *näin talon*.',
      compare: 'Kuten suomessa; valyriassa sivulause tulee tavallisesti päälauseen verbin edelle.',
    },
    en: {
      name: 'main clause',
      short: 'A **main clause** can stand alone, like *I saw the house*.',
      compare: 'As in Finnish; in High Valyrian a subordinate clause usually comes before the main clause’s verb.',
    },
    related: ['sivulause', 'relatiivilause'],
    firstIn: 'subordinate',
  },
  sivulause: {
    fi: {
      name: 'sivulause',
      short: '**Sivulause** on lause, joka on osa toista lausetta, kuten *jos*-lause tai *että*-lause.',
      compare: 'Valyriassa ei ole *että*-sanaa, ja sivulause on tavallisesti päälauseen verbin edellä.',
      long: 'Sivulauseen verbi on usein subjunktiivissa: kuvitellussa ehdossa, tarkoituksessa ja toiveessa.',
    },
    en: {
      name: 'subordinate clause',
      short: 'A **subordinate clause** is part of another clause, such as an *if* clause or a *that* clause.',
      compare: 'High Valyrian has no word for ‘that’, and the subordinate clause usually comes before the main clause’s verb.',
      long: 'Its verb is often subjunctive: in imagined conditions, purpose and wishes.',
    },
    related: ['paalause', 'relatiivilause', 'subjunktiivi'],
    firstIn: 'subordinate',
  },
  relatiivilause: {
    fi: {
      name: 'relatiivilause',
      short: '**Relatiivilause** on sivulause, joka kuvaa substantiivia, kuten suomen *joka*-lause: *mies, jonka näin*.',
      compare:
        'Valyriassa järjestys on suomen peilikuva: ensin lause, sitten relatiiviadjektiivi ja viimeisenä pääsana, kuten suomen partisiippimäärite *naista rohkaissut mies*.',
    },
    en: {
      name: 'relative clause',
      short: 'A **relative clause** is a subordinate clause that describes a noun, like an English *who* clause: *the man whom I saw*.',
      compare:
        'High Valyrian mirrors Finnish: first the clause, then the relative adjective and last the head, like the Finnish participle phrase *naista rohkaissut mies*.',
    },
    related: ['relatiiviadjektiivi', 'sivulause', 'paasana'],
    firstIn: 'relative',
  },
  relatiiviadjektiivi: {
    fi: {
      name: 'relatiiviadjektiivi',
      short: '**Relatiiviadjektiivi** on valyrian *joka*-sana [[lua]], joka mukautuu pääsanaansa kuten adjektiivi ja saa sijansa päälauseesta.',
      compare: 'Suomen relatiivipronomini *joka* saa sijansa omasta lauseestaan (*mies, jonka näin*); siksi valyrian sanaa kutsutaan adjektiiviksi.',
    },
    en: {
      name: 'relative adjective',
      short:
        'The **relative adjective** is the High Valyrian *who/which* word [[lua]], which agrees with its head like an adjective and takes its case from the main clause.',
      compare: 'Finnish *joka* takes its case from its own clause (*mies, jonka näin*); that is why the High Valyrian word is called an adjective.',
    },
    related: ['relatiivilause', 'mukautuminen'],
    firstIn: 'relative',
  },
  // Kielioppi upgrade step 2, group 3 (06-sentences, 07-words; audit 2026-10-07): terms the pages used unexplained.
  relatiivipronomini: {
    fi: {
      name: 'relatiivipronomini',
      short: '**Relatiivipronomini** on *joka*- tai *mikä*-sana, joka aloittaa relatiivilauseen, kuten suomen *joka*.',
      compare: 'Suomen *joka* seuraa pääsanaansa; valyrian [[lȳ]] ja [[līr]] tulevat ilman pääsanaa lauseen perään ja saavat sijansa päälauseesta.',
      long: 'Kun pääsana on mukana, valyria käyttää relatiiviadjektiivia [[lua]]. Pronomini [[lȳ]] on konkreettisille ja laskettaville asioille, myös ihmisille, ja [[līr]] abstrakteille asioille ja ainesanoille.',
    },
    en: {
      name: 'relative pronoun',
      short: 'A **relative pronoun** is a *who* or *which* word that starts a relative clause, like Finnish *joka*.',
      compare:
        'Finnish *joka* follows its head noun; High Valyrian [[lȳ]] and [[līr]] stand without a head, after the clause, and take their case from the main clause.',
      long: 'When there is a head noun, High Valyrian uses the relative adjective [[lua]]. The pronoun [[lȳ]] is for concrete, countable things, people included, and [[līr]] for abstract things and mass nouns.',
    },
    related: ['relatiivilause', 'relatiiviadjektiivi'],
    firstIn: 'relative',
  },
  'kylla-ei-kysymys': {
    fi: {
      name: 'kyllä–ei-kysymys',
      short: '**Kyllä–ei-kysymys** on kysymys, johon vastataan kyllä tai ei, kuten *tuletko huomenna?*',
      compare: 'Suomi merkitsee sen liitteellä *-ko/-kö*, eikä sävel yleensä nouse; valyriassa liitettä ei ole, vaan kysymyksen tekee nouseva sävelkulku.',
      long: 'Sanajärjestys on sama kuin väitteessä. Kielteinen kysymys voi olla merkitykseltään myönteinen, kuten suomen kohtelias *etkö tulisi kahville?*',
    },
    en: {
      name: 'yes–no question',
      short: 'A **yes–no question** is one answered with yes or no, like *are you coming tomorrow?*',
      compare:
        'Finnish marks it with *-ko/-kö* and usually does not raise the pitch; High Valyrian has no such ending, and the rising pitch makes the question.',
      long: 'The word order is that of a statement. A negative question can be positive in meaning, like the polite Finnish *etkö tulisi kahville?* ‘won’t you come for coffee?’',
    },
    related: ['kysymyssana', 'epasuora-kysymys'],
    firstIn: 'questions',
  },
  'epasuora-kysymys': {
    fi: {
      name: 'epäsuora kysymys',
      short: '**Epäsuora kysymys** on sivulauseeksi upotettu kysymys, kuten *en tiedä, tuleeko hän* tai *näin, minne hän meni*.',
      compare: 'Suomi käyttää kysymyssanaa tai liitettä *-ko/-kö*; valyrian kyllä–ei-kysymys alkaa sanalla [[lo]], ja verbi on subjunktiivissa.',
      long: 'Kysymyssanalla alkava epäsuora kysymys on tavallinen indikatiivilause, joka on päälauseen verbin edellä. Se ei ole relatiivilause, eikä se saa sijapäätettä.',
    },
    en: {
      name: 'indirect question',
      short: 'An **indirect question** is a question embedded as a subordinate clause, like *I don’t know whether he is coming* or *I saw where he went*.',
      compare: 'Finnish uses a question word or the ending *-ko/-kö*; a High Valyrian yes–no one starts with [[lo]], and the verb is subjunctive.',
      long: 'One that starts with a question word is an ordinary indicative clause standing before the main verb. It is not a relative clause and takes no case ending.',
    },
    related: ['kylla-ei-kysymys', 'kysymyssana', 'sivulause'],
    firstIn: 'subordinate',
  },
  sidesana: {
    fi: {
      name: 'sidesana',
      short: '**Sidesana** eli konjunktio liittää sanoja tai lauseita toisiinsa: *ja*, *tai*, *jos*, *koska*.',
      compare: 'Valyriassa ’ja’ on usein pelkkä pidennys sanan lopussa, ja moni sivulauseen sidesana vaatii verbin subjunktiiviin.',
    },
    en: {
      name: 'conjunction',
      short: 'A **conjunction** joins words or clauses: *and*, *or*, *if*, *because*.',
      compare:
        'In High Valyrian ‘and’ is often just a lengthening at the end of a word, and many conjunctions of subordinate clauses put the verb in the subjunctive.',
    },
    related: ['rinnastus', 'konjunktiivinen-pidennys', 'sivulause'],
    firstIn: 'subordinate',
  },
  taydennys: {
    fi: {
      name: 'täydennys',
      short: '**Täydennys** on lauseenjäsen, jonka verbi vaatii, kuten objekti tai se, jolle jotakin annetaan.',
      compare: 'Kuten suomessa; täydennys on eri asia kuin määre, jonka voi jättää pois lauseen hajoamatta.',
    },
    en: {
      name: 'complement',
      short: 'A **complement** is a clause element the verb requires, such as the object or the one something is given to.',
      compare: 'As in Finnish; a complement is not an adjunct, which can be left out without breaking the clause.',
    },
    related: ['valenssi', 'objekti', 'maare'],
    firstIn: 'valence',
  },
  maare: {
    fi: {
      name: 'määre',
      short: '**Määre** eli adverbiaali kertoo, missä, milloin, miten tai kenen hyväksi jotakin tehdään.',
      compare: 'Sama kuin suomen koulukieliopin adverbiaali (*kotona*, *eilen*, *ystävän puolesta*); valyrian applikatiivi voi tehdä määreestä objektin.',
    },
    en: {
      name: 'adjunct',
      short: 'An **adjunct** (adverbial) tells where, when, how or for whom something is done.',
      compare:
        'The same as the Finnish school-grammar adverbial (*kotona*, *eilen*, *ystävän puolesta*); a High Valyrian applicative can turn an adjunct into an object.',
    },
    related: ['taydennys', 'applikatiivi', 'adverbi'],
    firstIn: 'valence',
  },
});
